import type { LayoutDocument, LayoutNode } from "../layout/types.js";
import type { RenderNode } from "../layout-compiler/types.js";
import {
  designTokens,
  interfaceFont,
  type InterfaceColorMode,
} from "./tokens.js";
import type { InterfaceDesign } from "./visual-variants.js";

type Path = (string | number)[];
type Style = Record<string, string | number>;
export type CompositionRole =
  | "cell"
  | "header"
  | "tooltip"
  | "grid"
  | "form"
  | "modal"
  | "web"
  | "output";
export type ActionAppearance = {
  backgroundColor: string;
  color: string;
  activeBackgroundColor?: string;
};
export interface CompositionPatch {
  /** Paint selected after Behavior evaluation, only for explicitly mapped status colors. */
  runtimeColors?: Record<string, Style>;
  /** Group-list actions keyed by their stable catalogue key, never their translated label. */
  actionStyles?: Record<string, ActionAppearance>;
  canvas?: { width: number; height: number };
  backgroundColor?: string;
  nodes: Record<
    string,
    { x?: number; y?: number; width?: number; height?: number; style?: Style }
  >;
}
export interface CompositionDesign {
  version: 1;
  sourceStructure: string;
  light: CompositionPatch;
  dark: CompositionPatch;
}
export type DesignedDocument = LayoutDocument & {
  interfaceDesign?: CompositionDesign | null;
};
export interface CatalogueComposition {
  key: string;
  path: Path;
  label: string;
  role: CompositionRole;
  document: DesignedDocument;
  eligible: boolean;
  reason?: string;
}
export type CatalogueLike = {
  fields: ReadonlyArray<{
    key: string;
    label: string;
    scope: string;
    cellLayout?: unknown;
  }>;
  modals?: ReadonlyArray<{
    key: string;
    label: string;
    titleLayout?: unknown;
    contentLayout?: unknown;
    actionsLayout?: unknown;
  }>;
  displayLayouts?: Record<string, unknown> | null;
};
const object = (v: unknown): v is Record<string, any> =>
  !!v && typeof v === "object" && !Array.isArray(v);
const isDocument = (v: unknown): v is DesignedDocument =>
  object(v) &&
  object(v.meta) &&
  object(v.meta.canvas) &&
  object(v.scene) &&
  Array.isArray(v.scene.children);
const geometry = new Set(["x", "y", "width", "height"]);
const allowedStyles = new Set([
  "color",
  "backgroundColor",
  "fontFamily",
  "fontSize",
  "fontWeight",
  "textAlign",
  "border",
  "borderTop",
  "borderRight",
  "borderBottom",
  "borderLeft",
  "borderRadius",
  "padding",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "boxShadow",
  "lineHeight",
  "letterSpacing",
]);
const safeKey = (s: string) =>
  !["__proto__", "constructor", "prototype"].includes(s);
const positive = (v: unknown) =>
  typeof v === "number" && Number.isFinite(v) && v > 0 && v <= 10000;
export function validCompositionPatch(
  value: unknown,
): value is CompositionPatch {
  if (
    !object(value) ||
    !object(value.nodes) ||
    Object.keys(value).some(
      (k) =>
        ![
          "nodes",
          "canvas",
          "backgroundColor",
          "runtimeColors",
          "actionStyles",
        ].includes(k),
    )
  )
    return false;
  if (
    value.runtimeColors !== undefined &&
    (!object(value.runtimeColors) ||
      !Object.entries(value.runtimeColors).every(
        ([key, style]) =>
          safeKey(key) &&
          object(style) &&
          Object.entries(style).every(
            ([k, v]) =>
              allowedStyles.has(k) &&
              (typeof v === "string" ||
                (typeof v === "number" && Number.isFinite(v))),
          ),
      ))
  )
    return false;
  if (
    value.actionStyles !== undefined &&
    (!object(value.actionStyles) ||
      !Object.entries(value.actionStyles).every(
        ([key, style]) =>
          safeKey(key) &&
          object(style) &&
          typeof style.backgroundColor === "string" &&
          typeof style.color === "string" &&
          Object.entries(style).every(
            ([k, v]) =>
              ["backgroundColor", "color", "activeBackgroundColor"].includes(
                k,
              ) && typeof v === "string",
          ),
      ))
  )
    return false;
  if (
    value.backgroundColor !== undefined &&
    typeof value.backgroundColor !== "string"
  )
    return false;
  if (
    value.canvas !== undefined &&
    (!object(value.canvas) ||
      Object.keys(value.canvas).some((k) => !["width", "height"].includes(k)) ||
      !positive(value.canvas.width) ||
      !positive(value.canvas.height))
  )
    return false;
  return Object.entries(value.nodes).every(
    ([key, patch]) =>
      safeKey(key) &&
      object(patch) &&
      Object.entries(patch).every(([k, v]) =>
        geometry.has(k)
          ? typeof v === "number" &&
            Number.isFinite(v) &&
            (["x", "y"].includes(k) || positive(v))
          : k === "style" &&
            object(v) &&
            Object.entries(v).every(
              ([s, x]) =>
                allowedStyles.has(s) &&
                (typeof x === "string" ||
                  (typeof x === "number" && Number.isFinite(x))) &&
                (s !== "fontSize" || positive(x)) &&
                (s !== "fontWeight" || typeof x === "string"),
            ),
      ),
  );
}
export function validCompositionDesign(
  value: unknown,
): value is CompositionDesign {
  return (
    object(value) &&
    value.version === 1 &&
    typeof value.sourceStructure === "string" &&
    Object.keys(value).every((k) =>
      ["version", "sourceStructure", "light", "dark"].includes(k),
    ) &&
    validCompositionPatch(value.light) &&
    validCompositionPatch(value.dark)
  );
}

/** Node paths include list templates, but stop at tooltips: those are independent documents. */
export function compositionNodes(
  document: LayoutDocument,
): { key: string; path: Path; node: LayoutNode }[] {
  const result: { key: string; path: Path; node: LayoutNode }[] = [];
  const walk = (node: LayoutNode, path: Path) => {
    result.push({ key: JSON.stringify(path), path, node });
    if ("children" in node)
      node.children.forEach((child, i) =>
        walk(child, [...path, "children", i]),
      );
    if (node.kind === "component" && node.component === "layoutList") {
      const props = node.props as any;
      if (props?.rowTemplate)
        walk(props.rowTemplate, [...path, "props", "rowTemplate"]);
      props?.rowVariants?.forEach((v: any, i: number) => {
        if (v.template)
          walk(v.template, [...path, "props", "rowVariants", i, "template"]);
      });
    }
  };
  document.scene.children.forEach((node, i) =>
    walk(node, ["scene", "children", i]),
  );
  return result;
}
export function compositionStructure(document: LayoutDocument): string {
  return JSON.stringify(
    compositionNodes(document).map(({ key, node }) => [
      key,
      node.id,
      node.kind,
      (node as any).primitive ?? (node as any).component,
      node.label,
      (node as any).props?.fieldKey,
      (node as any).props?.key,
    ]),
  );
}
export function compatibleCompositionDesign(
  document: DesignedDocument,
  candidate: unknown = document.interfaceDesign,
): candidate is CompositionDesign {
  if (
    !validCompositionDesign(candidate) ||
    candidate.sourceStructure !== compositionStructure(document)
  )
    return false;
  const keys = new Set(compositionNodes(document).map((n) => n.key));
  return [candidate.light, candidate.dark].every((p) =>
    Object.keys(p.nodes).every((k) => keys.has(k)),
  );
}
function updateAt<T>(value: T, path: Path, change: (v: any) => any): T {
  if (!path.length) return change(value);
  const [head, ...rest] = path;
  const result: any = Array.isArray(value) ? [...value] : { ...value };
  result[head] = updateAt(result[head], rest, change);
  return result;
}

/** Walk configurations, never Behavior graphs or already generated visual metadata. */
export function catalogueCompositions(
  catalogue: CatalogueLike,
): CatalogueComposition[] {
  const result: CatalogueComposition[] = [];
  const walk = (
    value: unknown,
    path: Path,
    key: string,
    label: string,
    role: CompositionRole,
    eligible = true,
    reason?: string,
  ) => {
    if (!value || typeof value !== "object") return;
    if (isDocument(value))
      result.push({
        path,
        key,
        label,
        role,
        eligible,
        reason,
        document: value,
      });
    for (const [k, child] of Object.entries(value)) {
      if (
        ["behavior", "catalog", "interfaceDesign", "designVariants"].includes(k)
      )
        continue;
      if (Array.isArray(value)) {
        const identity = object(child) ? (child.key ?? child.id ?? k) : k;
        walk(
          child,
          [...path, Number(k)],
          `${key}/@${encodeURIComponent(identity)}`,
          ["columns", "categoryColumns"].includes(
            String(path[path.length - 1]),
          ) && object(child)
            ? `${label} · ${child.label ?? child.fieldKey ?? child.key ?? k}`
            : label,
          role,
          eligible,
          reason,
        );
      } else {
        const tooltip = k === "tooltip";
        const nextRole = !eligible
          ? role
          : tooltip
            ? "tooltip"
            : ["headerLayout", "headerGroup"].includes(k)
              ? "header"
              : role === "web" &&
                  k === "presentation" &&
                  path[path.length - 1] !== "toolbar"
                ? "grid"
                : role;
        walk(
          child,
          [...path, k],
          `${key}/${encodeURIComponent(k)}`,
          tooltip
            ? `${label} · Infobulle (${(value as any).label ?? (value as any).id ?? "détail"})`
            : ["headerLayout", "headerGroup"].includes(k)
              ? `${label} · ${k === "headerLayout" ? "En-tête" : "Groupe de colonnes"}`
              : role === "web" && k === "toolbar"
                ? `${label} · Barre de commandes`
                : role === "web" &&
                    k === "presentation" &&
                    path[path.length - 1] !== "toolbar"
                  ? `${label} · Présentation`
                  : label,
          nextRole,
          eligible,
          reason,
        );
      }
    }
  };
  catalogue.fields.forEach((f, i) => {
    const cell = f.cellLayout as any;
    if (!object(cell)) return;
    const base = `fields/${encodeURIComponent(f.scope)}/${encodeURIComponent(f.key)}`;
    for (const section of ["layout", "headerLayout"] as const)
      walk(
        cell[section],
        ["fields", i, "cellLayout", section],
        `${base}/${section}`,
        `${f.label} · ${section === "layout" ? "Cellule" : "En-tête"}`,
        section === "layout" ? "cell" : "header",
      );
    walk(
      cell.headerGroup?.layout,
      ["fields", i, "cellLayout", "headerGroup", "layout"],
      `${base}/headerGroup`,
      `${f.label} · Groupe de colonnes`,
      "header",
    );
  });
  (catalogue.modals ?? []).forEach((m, i) => {
    for (const section of [
      "titleLayout",
      "contentLayout",
      "actionsLayout",
    ] as const) {
      const output =
        section === "contentLayout" &&
        ["tickets", "attempt-tickets", "results"].includes(m.key);
      walk(
        m[section],
        ["modals", i, section],
        `modals/${encodeURIComponent(m.key)}/${section}`,
        `${m.label} · ${section === "titleLayout" ? "Titre" : section === "contentLayout" ? "Contenu" : "Actions"}`,
        output ? "output" : "modal",
        !output,
        output ? "Document imprimable conservé" : undefined,
      );
    }
  });
  for (const [key, value] of Object.entries(catalogue.displayLayouts ?? {})) {
    const eligible = [
      "group_grid",
      "athlete_grid",
      "group_form",
      "athlete_form",
      "web_view",
      "results_view",
    ].includes(key);
    const role: CompositionRole = key.endsWith("_grid")
      ? "grid"
      : key.endsWith("_form")
        ? "form"
        : eligible
          ? "web"
          : "output";
    walk(
      value,
      ["displayLayouts", key],
      `displayLayouts/${encodeURIComponent(key)}`,
      key,
      role,
      eligible,
      eligible ? undefined : "Sortie diffusée ou export conservé",
    );
  }
  return result;
}
function controlledStyleKeys(document: LayoutDocument): Set<string> {
  const keys = new Set<string>();
  const visit = (value: unknown) => {
    if (!value || typeof value !== "object") return;
    if (
      object(value) &&
      value.type === "action" &&
      typeof value.key === "string"
    )
      keys.add(value.key);
    for (const [key, child] of Object.entries(value))
      if (key !== "interfaceDesign" && key !== "tooltip") visit(child);
  };
  visit(document.scene);
  return keys;
}
const normal = (value: unknown) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/\s/g, "");
const whites = new Set(["white", "#fff", "#ffffff", "rgb(255,255,255)"]);
const darks = new Set([
  "black",
  "#000",
  "#000000",
  "#172033",
  "#111827",
  "#222",
  "#222222",
  "#263238",
  "rgba(0,0,0,.87)",
  "rgba(0,0,0,0.87)",
]);
const muted = new Set([
  "#666",
  "#666666",
  "#777",
  "#757575",
  "rgba(0,0,0,0.6)",
]);
const fills = new Set([
  "#f2f2f2",
  "rgb(242,242,242)",
  "#e3e3e3",
  "#f5f5f5",
  "#eee",
  "#eeeeee",
  "#eef2f6",
  "#e8eef7",
  "rgba(0,0,0,0.04)",
]);
const accents = new Set(["#1976d2", "#0056b3", "#1565c0", "#0288d1"]);

/** Small colored labels carry status information rather than interface chrome. */
export function isCompositionStatusIndicator(node: LayoutNode): boolean {
  return (
    node.kind === "primitive" &&
    node.primitive === "text" &&
    node.width <= 32 &&
    node.height <= 24 &&
    !!node.style?.backgroundColor
  );
}

/** Static chrome only. Sport colors, conditional styles and Behavior-driven properties stay authoritative. */
export function createCompositionDesign(
  entry: CatalogueComposition,
): CompositionDesign {
  const document = entry.document;
  const headerGroup =
    entry.role === "header" && entry.path.includes("headerGroup");
  const nodes = compositionNodes(document);
  const controlled = controlledStyleKeys(document);
  const build = (mode: InterfaceColorMode): CompositionPatch => {
    const p = designTokens[mode];
    const patch: CompositionPatch = { nodes: {} };
    if (entry.key === "displayLayouts/group_grid") {
      patch.actionStyles = {
        "open-group": { backgroundColor: p.accent, color: p.onAccent },
        "start-group": {
          backgroundColor: p.success,
          activeBackgroundColor: p.warning,
          color: p.onAccent,
        },
        exports: { backgroundColor: p.accent, color: p.onAccent },
        synchronise: { backgroundColor: p.accent, color: p.onAccent },
        powerscout: { backgroundColor: "transparent", color: p.muted },
      };
    }
    if (entry.key === "fields/GROUP/status/layout") {
      patch.runtimeColors = Object.fromEntries(
        [
          ["#2e7d32", p.success],
          ["#1976d2", p.accent],
          ["#0288d1", p.muted],
          ["#ed6c02", p.warning],
        ].map(([source, color]) => [
          source,
          {
            backgroundColor: `color-mix(in srgb, ${color} 10%, ${p.surface})`,
            color,
            borderRadius: 5,
            fontSize: 11,
            fontWeight: "500",
          },
        ]),
      );
    }
    const neutral = (v: unknown, background: boolean, onAccent = false) => {
      const c = normal(v);
      if (whites.has(c))
        return background ? p.surface : onAccent ? p.onAccent : p.text;
      if (darks.has(c)) return background ? p.chrome : p.text;
      if (muted.has(c)) return background ? p.fill : p.muted;
      if (fills.has(c)) return background ? p.fill : p.muted;
      if (accents.has(c)) return p.accent;
      return v;
    };
    if (document.scene.backgroundColor)
      patch.backgroundColor = neutral(
        document.scene.backgroundColor,
        true,
      ) as string;
    if (entry.role === "header")
      patch.backgroundColor = headerGroup ? p.accent : p.chrome;
    for (const { key, node } of nodes) {
      const s = node.style ?? {};
      const style: Style = {};
      const preserveMedia =
        (node.kind === "primitive" &&
          ["image", "video"].includes(node.primitive)) ||
        (node.kind === "primitive" &&
          node.primitive === "shape" &&
          Math.max(node.width, node.height) <= 100) ||
        (node.kind === "component" &&
          ["qrCode", "barLoader"].includes(node.component));
      if (preserveMedia || isCompositionStatusIndicator(node)) continue;
      const text =
        (node.kind === "primitive" && node.primitive === "text") ||
        (node.kind === "component" &&
          ["customDisciplineInput", "customDisciplineButton"].includes(
            node.component,
          ));
      if (text) {
        style.fontFamily = interfaceFont;
        if (node.kind === "primitive" && s.color === undefined)
          style.color = p.text;
        if (node.kind === "primitive" && s.fontSize === undefined)
          style.fontSize = 12;
        if (typeof s.fontSize === "number")
          style.fontSize =
            s.fontSize >= 18
              ? Math.max(16, Math.round(s.fontSize * 0.85))
              : Math.max(10, Math.min(12, s.fontSize));
        if (s.fontWeight !== undefined) style.fontWeight = String(s.fontWeight);
      }
      for (const k of ["color", "backgroundColor"] as const)
        if (s[k] !== undefined)
          style[k] = neutral(
            s[k],
            k === "backgroundColor",
            accents.has(normal(s.backgroundColor)),
          ) as string;
      for (const k of [
        "border",
        "borderTop",
        "borderRight",
        "borderBottom",
        "borderLeft",
      ] as const) {
        const value = s[k];
        if (
          typeof value === "string" &&
          /(?:#(?:ddd|ccc|e0e0e0|ddd|000000)|rgba?\(0,\s*0,\s*0|black|#e3e3e3)/i.test(
            value,
          )
        )
          style[k] = `1px solid ${p.fine}`;
      }
      if (s.borderRadius !== undefined)
        style.borderRadius = Math.min(6, Number(s.borderRadius) || 0);
      if (s.boxShadow !== undefined) style.boxShadow = "none";
      if (entry.role === "header" && text) {
        style.fontSize = 11;
        style.fontWeight = "500";
        style.color = headerGroup ? p.onAccent : p.muted;
        style.backgroundColor = headerGroup ? p.accent : p.chrome;
      }
      if (entry.role === "web" && node.id === "toolbar") {
        style.backgroundColor = p.chrome;
        style.color = p.text;
      }
      if (entry.role === "grid") {
        const selected = [
          "selected-row",
          "hover-selected-row",
          "active-row",
          "hover-active-row",
        ].includes(node.id);
        if (selected) style.backgroundColor = p.selected;
        if (["hover-row", "hover-cell"].includes(node.id))
          style.backgroundColor = p.fill;
        if (["column-header", "toolbar", "footer"].includes(node.id)) {
          style.backgroundColor = p.chrome;
          style.color = p.muted;
        }
        if (node.id === "selected-cell")
          style.boxShadow = `inset 0 0 0 1px ${p.accent}`;
        if (node.id === "editing-cell") {
          style.backgroundColor = p.surface;
          style.color = p.text;
        }
      }
      const filtered = Object.fromEntries(
        Object.entries(style).filter(
          ([k, v]) => !controlled.has(k) && v !== (s as any)[k],
        ),
      );
      if (Object.keys(filtered).length) patch.nodes[key] = { style: filtered };
    }
    // Compact only simple static headers. Dense cells and forms retain their authored geometry.
    if (
      entry.role === "header" &&
      nodes.length === 1 &&
      document.meta.canvas.height >= 34 &&
      document.meta.canvas.height <= 70 &&
      !controlled.has("height") &&
      !controlled.has("y")
    ) {
      patch.canvas = { width: document.meta.canvas.width, height: 34 };
      const n = nodes[0];
      if (n.node.y === 0 && n.node.height === document.meta.canvas.height)
        patch.nodes[n.key] = { ...patch.nodes[n.key], height: 34 };
    }
    if (entry.role === "grid")
      for (const n of nodes)
        if (n.node.id === "column-header")
          patch.nodes[n.key] = { ...patch.nodes[n.key], height: 34 };
    return patch;
  };
  return {
    version: 1,
    sourceStructure: compositionStructure(document),
    light: build("light"),
    dark: build("dark"),
  };
}

/** This is a preview materialization, never a replacement for the persisted original. */
export function presentComposition(
  document: DesignedDocument,
  design: InterfaceDesign,
  mode: InterfaceColorMode,
  candidate: unknown = document.interfaceDesign,
): LayoutDocument {
  if (
    design === "original" ||
    !compatibleCompositionDesign(document, candidate)
  )
    return document;
  const patch = candidate[mode];
  let result: DesignedDocument = {
    ...document,
    meta: { ...document.meta, canvas: patch.canvas ?? document.meta.canvas },
    scene: {
      ...document.scene,
      backgroundColor: patch.backgroundColor ?? document.scene.backgroundColor,
    },
  };
  for (const { key, path } of compositionNodes(document)) {
    const value = patch.nodes[key];
    if (value)
      result = updateAt(result, path, (node) => ({
        ...node,
        ...value,
        style: { ...node.style, ...value.style },
      }));
  }
  return result;
}
/** Copy on write, preserving all original nodes, values and graphs. */
export function applyCatalogueDesigns<T extends CatalogueLike>(
  catalogue: T,
  designs: Record<string, CompositionDesign | null>,
): T {
  let result = catalogue;
  for (const entry of catalogueCompositions(catalogue)) {
    if (
      !entry.eligible ||
      !Object.prototype.hasOwnProperty.call(designs, entry.key)
    )
      continue;
    const design = designs[entry.key];
    if (design !== null && !compatibleCompositionDesign(entry.document, design))
      continue;
    result = updateAt(result, entry.path, (document) => ({
      ...document,
      interfaceDesign: design,
    }));
  }
  return result;
}
export function prepareCatalogueDesigns(
  catalogue: CatalogueLike,
): Record<string, CompositionDesign | null> {
  return Object.fromEntries(
    catalogueCompositions(catalogue)
      .filter((e) => e.eligible)
      .map((e) => [
        e.key,
        e.document.interfaceDesign === null
          ? null
          : compatibleCompositionDesign(e.document)
            ? e.document.interfaceDesign!
            : createCompositionDesign(e),
      ]),
  );
}

type OperatorCatalogue = Omit<CatalogueLike, "displayLayouts"> & {
  displayLayouts?: unknown;
};
const defaultOperatorCatalogues = new WeakMap<
  OperatorCatalogue,
  OperatorCatalogue
>();
/** Modern operator defaults are additive; explicit administrator choices and outputs win. */
export function withDefaultOperatorDesigns<T extends OperatorCatalogue>(
  catalogue: T,
  mode: InterfaceDesign,
): T {
  if (mode !== "modern") return catalogue;
  const cached = defaultOperatorCatalogues.get(catalogue);
  if (cached) return cached as T;
  const parse = (value: unknown) => {
    if (typeof value !== "string") return value;
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };
  const layouts = parse(catalogue.displayLayouts);
  const normalized = {
    ...catalogue,
    fields: catalogue.fields.map((field) => ({
      ...field,
      cellLayout: parse(field.cellLayout),
    })),
    modals: catalogue.modals?.map((modal) => ({
      ...modal,
      titleLayout: parse(modal.titleLayout),
      contentLayout: parse(modal.contentLayout),
      actionsLayout: parse(modal.actionsLayout),
    })),
    displayLayouts: object(layouts) ? layouts : undefined,
  };
  const missing = catalogueCompositions(normalized).filter(
    (entry) =>
      entry.eligible &&
      !Object.prototype.hasOwnProperty.call(entry.document, "interfaceDesign"),
  );
  const result = applyCatalogueDesigns(
    normalized,
    Object.fromEntries(
      missing.map((entry) => [entry.key, createCompositionDesign(entry)]),
    ),
  );
  defaultOperatorCatalogues.set(catalogue, result);
  return result as unknown as T;
}
/** Preview nested tooltips with their own variants. Never walks Behavior programs. */
export function previewCatalogueDocument(
  document: DesignedDocument,
  design: InterfaceDesign,
  mode: InterfaceColorMode,
): LayoutDocument {
  if (design === "original") return document;
  const walk = (v: any): any => {
    if (!v || typeof v !== "object") return v;
    if (Array.isArray(v)) return v.map(walk);
    const current = isDocument(v) ? presentComposition(v, design, mode) : v;
    return Object.fromEntries(
      Object.entries(current).map(([k, child]) => [
        k,
        ["behavior", "catalog", "interfaceDesign"].includes(k)
          ? child
          : walk(child),
      ]),
    );
  };
  return walk(document);
}

const runtimeColorKeys = new WeakMap<
  LayoutDocument,
  Map<string, Set<string>>
>();
/** Translate neutral paint emitted by Behavior, after evaluation. Never changes program values or effects. */
export function presentCompositionRuntimeColors(
  nodes: RenderNode[],
  document: LayoutDocument,
  mode?: InterfaceColorMode,
): RenderNode[] {
  if (!mode) return nodes;
  const palette = designTokens[mode];
  let dynamic = runtimeColorKeys.get(document);
  if (!dynamic) {
    dynamic = new Map(
      compositionNodes(document).map(({ node }) => [
        node.id,
        controlledStyleKeys({
          ...document,
          scene: { ...document.scene, children: [node] },
        }),
      ]),
    );
    runtimeColorKeys.set(document, dynamic);
  }
  const neutralBackground = (value: unknown) => {
    const color = normal(value);
    return (
      !color ||
      color === "transparent" ||
      whites.has(color) ||
      darks.has(color) ||
      fills.has(color) ||
      [
        palette.surface,
        palette.chrome,
        palette.fill,
        palette.selected,
      ].includes(color as never)
    );
  };
  const runtimeDesign = (document as DesignedDocument).interfaceDesign;
  const runtimePaint = compatibleCompositionDesign(document, runtimeDesign)
    ? runtimeDesign[mode].runtimeColors
    : undefined;
  const visit = (
    node: RenderNode,
    inheritedSemanticBackground: string | undefined,
  ): RenderNode => {
    const semanticBackground = neutralBackground(node.style.backgroundColor)
      ? inheritedSemanticBackground
      : String(node.style.backgroundColor);
    const style = { ...node.style };
    if (node.kind === "text" && style.color === undefined)
      style.color = palette.text;
    const keys = dynamic.get(node.id);
    if (!["shape", "image", "video", "icon"].includes(node.kind)) {
      if (keys?.has("backgroundColor")) {
        const color = normal(style.backgroundColor);
        if (whites.has(color)) style.backgroundColor = palette.surface;
        else if (darks.has(color)) style.backgroundColor = palette.chrome;
        else if (fills.has(color)) style.backgroundColor = palette.fill;
      }
      if (keys?.has("color")) {
        const color = normal(style.color);
        if (darks.has(color)) style.color = palette.text;
        else if (whites.has(color) && !semanticBackground)
          style.color = palette.text;
        else if (muted.has(color)) style.color = palette.muted;
      }
    }
    if (node.kind === "text" && semanticBackground) {
      const background = normal(semanticBackground);
      const hex = background.match(/^#([a-f0-9]{6}|[a-f0-9]{3})$/i)?.[1];
      const rgb = hex
        ? hex.length === 3
          ? hex.split("").map((value) => parseInt(value + value, 16))
          : [0, 2, 4].map((index) => parseInt(hex.slice(index, index + 2), 16))
        : background
            .match(/^rgb\((\d+),(\d+),(\d+)\)$/)
            ?.slice(1)
            .map(Number);
      const foreground = normal(style.color);
      if (
        rgb &&
        (darks.has(foreground) ||
          whites.has(foreground) ||
          foreground === normal(palette.text) ||
          foreground === normal(palette.muted))
      ) {
        const brightness = rgb.reduce(
          (sum, value, index) => sum + value * [0.2126, 0.7152, 0.0722][index],
          0,
        );
        if (brightness > 145 && !darks.has(foreground)) style.color = "#172033";
        else if (brightness <= 145 && !whites.has(foreground))
          style.color = "#f5f5f7";
      }
    }
    const mapped = runtimePaint?.[normal(node.style.backgroundColor)];
    if (node.kind === "text" && mapped) Object.assign(style, mapped);
    return {
      ...node,
      style,
      ...("children" in node
        ? {
            children: node.children.map((child) =>
              visit(child, semanticBackground),
            ),
          }
        : {}),
    } as RenderNode;
  };
  const design = (document as DesignedDocument).interfaceDesign;
  const sceneBackground = validCompositionDesign(design)
    ? (design[mode].backgroundColor ?? document.scene.backgroundColor)
    : document.scene.backgroundColor;
  return nodes.map((node) =>
    visit(
      node,
      neutralBackground(sceneBackground) ? undefined : sceneBackground,
    ),
  );
}

/** The catalogue variant owns paint; callers retain visibility, pending state and callbacks. */
export function readGroupActionAppearance(
  document: unknown,
  key: string,
  mode: InterfaceDesign,
  color: InterfaceColorMode,
  active = false,
): ActionAppearance | undefined {
  if (
    mode !== "modern" ||
    !isDocument(document) ||
    !compatibleCompositionDesign(document)
  )
    return undefined;
  const style = document.interfaceDesign![color].actionStyles?.[key];
  return style
    ? {
        ...style,
        backgroundColor: active
          ? (style.activeBackgroundColor ?? style.backgroundColor)
          : style.backgroundColor,
      }
    : undefined;
}
